import { useState } from "react";
import { Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { ADMIN_SOLAR_SITES } from "@/constants/testIds";
import { formatApiError, linkSolarSiteProject, searchErpProjects } from "@/lib/api";

const MIN_QUERY = 3;

// The API reports a link refusal by its reason code; these are the readable
// versions. Anything else falls back to whatever the API said.
const LINK_ERRORS = {
  plant_already_linked: "Unlink the current ERP Project first",
  project_already_linked: "That ERP Project already belongs to another site",
  project_has_no_customer: "This Project has no customer in ERP, so support cannot use it",
  project_not_found: "That ERP Project is missing in ERP",
  erp_unavailable: "ERP could not be reached, please try again",
};

// Mapping a site to its ERP Project is what enables support for that site, so
// the picker searches ERP live rather than trusting a typed Project id.
export const ErpProjectLinkDialog = ({ site, open, onOpenChange, onLinked }) => {
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(null);

  const reset = (next) => {
    if (!next) {
      setTerm("");
      setRows([]);
      setSearched(false);
    }
    onOpenChange(next);
  };

  const search = async (event) => {
    event.preventDefault();
    const query = term.trim();
    if (query.length < MIN_QUERY) {
      toast.error("Enter at least three characters to search ERP");
      return;
    }
    setSearching(true);
    try {
      setRows(await searchErpProjects(query));
      setSearched(true);
    } catch (error) {
      toast.error(formatApiError(error, "Could not search ERP Projects"));
    } finally {
      setSearching(false);
    }
  };

  const link = async (project) => {
    setLinking(project);
    try {
      const result = await linkSolarSiteProject(site.id, project);
      toast.success(`${site.name} is linked to ${project}`);
      reset(false);
      await onLinked(result);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(
        LINK_ERRORS[detail] || formatApiError(error, "Could not link this ERP Project"),
      );
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-lg" data-testid={ADMIN_SOLAR_SITES.projectDialog}>
        <DialogHeader>
          <DialogTitle>Link ERP Project</DialogTitle>
          <DialogDescription>
            Search the ERP Project that carries this installation. Support opens for{" "}
            {site?.name} once it is mapped. Search by Project id, project name or
            customer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={search} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-subtle" />
            <Input
              data-testid={ADMIN_SOLAR_SITES.projectSearchInput}
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Project id, project name or customer"
              className="h-10 rounded-[10px] border-black/10 bg-white pl-9"
            />
          </div>
          <Button
            data-testid={ADMIN_SOLAR_SITES.projectSearchButton}
            type="submit"
            disabled={searching}
            className="h-10 rounded-[10px] bg-brand-forest text-white"
          >
            {searching ? <Loader2 className="animate-spin" size={16} /> : "Search"}
          </Button>
        </form>
        <div className="max-h-[320px] space-y-2 overflow-y-auto">
          {rows.map((row) => (
            <div
              key={row.name}
              data-testid={ADMIN_SOLAR_SITES.projectResult}
              className="flex flex-wrap items-center justify-between gap-3 rounded-[12px] border border-black/5 bg-brand-surface p-3"
            >
              <div className="min-w-0">
                <b className="text-[14px]">{row.project_name || row.name}</b>
                <div className="mt-1 text-[12px] text-brand-muted">
                  {[row.name, row.customer].filter(Boolean).join(" · ")}
                </div>
                <span className="text-[11px] font-semibold text-brand">
                  {row.status || "Status unavailable"}
                </span>
              </div>
              <Button
                data-testid={`${ADMIN_SOLAR_SITES.projectLinkButton}-${row.name}`}
                size="sm"
                disabled={linking !== null}
                onClick={() => link(row.name)}
              >
                Link
              </Button>
            </div>
          ))}
          {searched && !rows.length && (
            <p className="empty-copy">No ERP Project matches that search.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
